import { FC } from 'react'
import { TextInput, useGetOne, useRecordContext, useTranslate } from 'react-admin'
import { useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { OrganizationWorkflowLinkOut, WorkflowOut } from '../../types/apiRequests'
import { themeColor } from '../../layout/theme/color'

const MONO_FONT = '"SF Mono", "Fira Code", ui-monospace, monospace'

interface DeclaredVariable {
  name?: string
  description?: string
}

const LinkVariablesSection: FC = () => {
  const record = useRecordContext<OrganizationWorkflowLinkOut>()
  const translate = useTranslate()

  // On create there is no record yet, so fall back to the workflow_id the user
  // selects in the form. This lets the section render in both Edit and Create.
  const watchedWorkflowId = useWatch({ name: 'workflow_id' }) as string | undefined
  const workflowId = record?.workflow_id ?? watchedWorkflowId ?? ''

  const { data: workflow } = useGetOne<WorkflowOut>(
    'workflow',
    { id: workflowId },
    { enabled: !!workflowId },
  )

  if (!workflowId) return null

  const variables = (workflow?.variables ?? []) as DeclaredVariable[]
  const validVariables = variables.filter((v): v is DeclaredVariable & { name: string } => !!v.name)

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 0.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: themeColor.slate, letterSpacing: '-0.01em' }}>
          {translate('resources.organization_workflow_link.variablesTitle')}
        </Typography>
        {validVariables.length > 0 && (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: themeColor.slate60,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {validVariables.length}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: themeColor.slate60, mb: 1.5, lineHeight: 1.5 }}>
        {translate('resources.organization_workflow_link.variablesSubtitle')}
      </Typography>

      {validVariables.length === 0 ? (
        <Box
          sx={{
            background: 'rgba(0,0,0,0.02)',
            borderRadius: '10px',
            border: '1px dashed rgba(0,0,0,0.10)',
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: themeColor.slate, lineHeight: 1.55 }}>
            {translate('resources.organization_workflow_link.variablesEmpty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {validVariables.map((v) => (
            <TextInput
              key={v.name}
              source={`variable_values.${v.name}`}
              label={
                <Box component="span" sx={{ fontFamily: MONO_FONT, fontSize: 13 }}>
                  {v.name}
                </Box>
              }
              helperText={v.description || translate('resources.organization_workflow_link.variableHelper')}
              fullWidth
              parse={(value: string) => (value === '' ? null : value)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default LinkVariablesSection
